import { legalAcceptanceRequired, throwIfLegalAcceptanceRequired } from "./legal_acceptance.js"
import { OFFLINE_CACHE_PREFIX, OfflinePdfStore, offlineCopyExpired } from "./offline_pdf_store.js"
import { validateManifest } from "./offline_pdf_download.js"

export async function pauseOfflinePdfs(store, record, acceptanceUrl) {
  const records = store.list ? await store.list(record.userId) : [record]
  const revision = crypto.randomUUID()
  record.legalAcceptanceRequired = acceptanceUrl
  record.legalAcceptanceRevision = revision
  if (record.key && !records.some((candidate) => candidate.key === record.key)) records.push(record)
  for (const candidate of records) {
    candidate.legalAcceptanceRequired = acceptanceUrl
    candidate.legalAcceptanceRevision = revision
    await store.putIfActive(candidate, { legalAcceptanceRequired: acceptanceUrl, legalAcceptanceRevision: revision })
  }
}

export async function findOfflinePdf({ store = new OfflinePdfStore(), userId, editionId }) {
  const records = await store.list(userId)
  const record = records.find((candidate) => candidate.editionId === editionId)
  if (!record) return null
  if (offlineCopyExpired(record)) {
    await store.delete(record)
    return null
  }
  if (!record.ready || !await store.verifyComplete(record)) return null
  return record
}

export async function reauthorizeOfflinePdf(record, manifestUrl, {
  store = new OfflinePdfStore(),
  fetch = globalThis.fetch,
  caches = globalThis.caches,
  location = globalThis.location
} = {}) {
  if (offlineCopyExpired(record)) {
    await store.delete(record)
    return { status: "expired", record: null }
  }

  const priorLegalAcceptanceRevision = record.legalAcceptanceRevision ?? null
  let response
  try {
    response = await fetch(manifestUrl, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" }
    })
  } catch (error) {
    if (error.name === "AbortError") throw error
    return record.legalAcceptanceRequired ? { status: "legal_acceptance_required", acceptanceUrl: record.legalAcceptanceRequired, record } : { status: "offline", record }
  }

  const legal = await legalAcceptanceRequired(response)
  if (legal) {
    await pauseOfflinePdfs(store, record, legal.acceptanceUrl)
    return { ...legal, record }
  }

  if ([401, 403, 404].includes(response.status)) {
    await store.delete(record)
    return { status: "denied", record: null }
  }
  if (!response.ok) return { status: "retry", record }

  let manifest
  try {
    manifest = validateManifest(await response.json())
  } catch (_error) {
    return { status: "retry", record }
  }
  if (manifest.user_id !== record.userId || manifest.edition.id !== record.editionId || manifest.edition.checksum !== record.checksum) {
    await store.delete(record)
    return { status: "stale", record: null }
  }

  const updatedRecord = {
    ...record,
    legalAcceptanceRequired: null,
    legalAcceptanceRevision: null,
    expiresAt: manifest.expires_at,
    documentUrl: manifest.document_url,
    readerUrl: manifest.reader_url,
    manifestUrl,
    progressUrl: manifest.reader_url,
    updatedAt: new Date().toISOString()
  }
  if (!updatedRecord.pendingProgress) updatedRecord.progress = manifest.progress
  if (offlineCopyExpired(updatedRecord)) {
    await store.delete(record)
    return { status: "expired", record: null }
  }

  const previousReaderUrl = record.readerUrl
  let readerCache
  if (previousReaderUrl && previousReaderUrl !== updatedRecord.readerUrl) {
    try {
      readerCache = await cacheOfflineReader(updatedRecord, {
        caches,
        fetch,
        location
      })
    } catch (error) {
      if (error.name === "AbortError") throw error
      if (error.code === "legal_acceptance_required") {
        await pauseOfflinePdfs(store, record, error.acceptanceUrl)
        return { status: error.code, acceptanceUrl: error.acceptanceUrl, record }
      }
      return { status: "retry", record }
    }
  }

  try {
    const saved = await store.putIfActive(updatedRecord, { reauthorizedLegalAcceptanceRevision: priorLegalAcceptanceRevision })
    if (["superseded", "missing"].includes(saved.status)) return { status: saved.status, record: null }
    Object.assign(updatedRecord, saved.record)
  } catch (_error) {
    return { status: "retry", record }
  }

  Object.assign(record, updatedRecord)
  if (record.legalAcceptanceRequired) {
    return { status: "legal_acceptance_required", acceptanceUrl: record.legalAcceptanceRequired, record }
  }

  if (readerCache) {
    try {
      await readerCache.delete(previousReaderUrl)
    } catch (_error) {
      // The committed record and its new cache remain usable; the obsolete entry is harmless.
    }
  }

  return { status: "authorized", record }
}

export async function refreshOfflinePdfs(userId, {
  store = new OfflinePdfStore(),
  fetch = globalThis.fetch,
  csrfToken = globalThis.document?.querySelector("meta[name='csrf-token']")?.content
} = {}) {
  let retry = false

  for (const record of await store.list(userId)) {
    const manifestUrl = record.manifestUrl || `/editions/${record.editionId}/offline_manifest.json`
    const access = await reauthorizeOfflinePdf(record, manifestUrl, { store, fetch })
    if (access.status === "legal_acceptance_required") return { ...access, retry: false }
    if (["offline", "retry"].includes(access.status)) {
      retry = true
      continue
    }
    if (access.status !== "authorized" || !access.record.pendingProgress) continue

    const progress = await syncOfflineProgress(access.record, { store, fetch, csrfToken })
    if (progress.status === "legal_acceptance_required") return { ...progress, retry: false }
    if (["pending", "retry"].includes(progress.status)) retry = true
  }

  return { retry }
}

export async function saveOfflineProgress(record, progress, { store = new OfflinePdfStore() } = {}) {
  const result = await store.saveProgressIfActive(record, progress, { pending: true })
  if (result.status === "saved") Object.assign(record, result.record)
  return result
}

export async function syncOfflineProgress(record, {
  store = new OfflinePdfStore(),
  fetch = globalThis.fetch,
  csrfToken = globalThis.document?.querySelector("meta[name='csrf-token']")?.content
} = {}) {
  const localRecord = record
  const active = await store.activeRecord(record)
  if (["superseded", "missing"].includes(active.status)) return { status: active.status, record: null }

  Object.assign(localRecord, active.record)
  record = localRecord
  if (!record.pendingProgress) return { status: "idle", record }

  // Keep the upload immutable so progress saved while the request is in flight
  // remains queued for the next synchronization attempt.
  const pendingProgress = structuredClone(record.pendingProgress)

  const headers = { Accept: "application/json", "Content-Type": "application/json" }
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken

  let response
  try {
    response = await fetch(record.progressUrl, {
      method: "PATCH",
      credentials: "same-origin",
      headers,
      body: JSON.stringify({ reading: pendingProgress })
    })
  } catch (_error) {
    return { status: "retry", record }
  }

  const legal = await legalAcceptanceRequired(response)
  if (legal) {
    await pauseOfflinePdfs(store, record, legal.acceptanceUrl)
    return { ...legal, record }
  }

  if ([401, 403, 404].includes(response.status)) {
    await store.delete(record)
    return { status: "denied", record: null }
  }
  if (!response.ok) return { status: "retry", record }

  const acknowledgement = await store.acknowledgePendingProgress(record, pendingProgress)
  if (["superseded", "missing"].includes(acknowledgement.status)) {
    return { status: acknowledgement.status, record: null }
  }
  if (acknowledgement.status !== "acknowledged") return { status: "pending", record: acknowledgement.record }

  Object.assign(record, acknowledgement.record)
  return { status: "synced", record }
}

export async function cacheOfflineReader(record, {
  caches = globalThis.caches,
  fetch = globalThis.fetch,
  location = globalThis.location
} = {}) {
  const response = await fetch(record.readerUrl, { credentials: "same-origin", headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" } })
  await throwIfLegalAcceptanceRequired(response)
  if (!response.ok) throw new Error(`Reader page request returned ${response.status}`)
  if (response.redirected) throw new Error("Reader page request was redirected")

  const contentType = response.headers.get("Content-Type")?.split(";", 1)[0].trim().toLowerCase()
  if (contentType !== "text/html") throw new Error("Reader page response was not HTML")

  const baseUrl = location?.href || response.url
  const requestedUrl = new URL(record.readerUrl, baseUrl)
  const responseUrl = new URL(response.url)
  const applicationOrigin = location?.origin || new URL(baseUrl).origin
  if (responseUrl.origin !== applicationOrigin ||
      responseUrl.origin !== requestedUrl.origin ||
      responseUrl.pathname !== requestedUrl.pathname ||
      responseUrl.search !== requestedUrl.search) {
    throw new Error("Reader page response URL did not match the request")
  }

  const cache = await caches.open(`${OFFLINE_CACHE_PREFIX}${record.userId}`)
  await cache.put(record.readerUrl, response)
  return cache
}
